import { useHubble } from '@seek/hubble';
import { useConfig } from '@seek/libs-shared';
import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { Hidden, Pagination, Stack } from 'braid-design-system';
import { useState } from 'react';
import { useParams } from 'react-router';
import { CompanyProfilesPage, ROOT_PATH } from '../../../../paths';
import type { SalaryRangeByRole } from '../../../pages/salaries/salariesTabModel';
import translations from './.vocab/index';
import { DesktopSalariesList } from './DesktopSalariesList';
import { MobileSalariesList } from './MobileSalariesList';

const PAGE_SIZE = 6;

export const SalariesList = ({
  scrollToTop,
  salaryRangeByRoles,
  isSalariesByRole = false,
}: {
  salaryRangeByRoles: SalaryRangeByRole[];
  scrollToTop: () => void;
  isSalariesByRole?: boolean;
}) => {
  const { t } = useTranslations(translations);
  const { language } = useConfig();
  const params = useParams();
  const localize = useMelwaysLink();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const hubble = useHubble();

  const totalPages = Math.ceil(salaryRangeByRoles.length / PAGE_SIZE);
  const paginatedSalaryRanges = salaryRangeByRoles.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const salariesPagePath = localize({
    path: `/${ROOT_PATH}/${params.companySlug}/${CompanyProfilesPage.Salaries}`,
    language,
  });

  const trackRoleClicked = (roleTitle: string) => {
    hubble.trackEvent('component_pressed', {
      component: {
        name: 'role salary card',
        text: roleTitle,
        type: 'card',
      },
      currentPageSection: isSalariesByRole ? 'salaries role' : 'salaries',
    });
  };

  return (
    <Stack space="large" data={{ testid: 'salaries-list' }}>
      <Hidden above="mobile">
        <MobileSalariesList
          paginatedSalaryRanges={paginatedSalaryRanges}
          trackRoleClicked={trackRoleClicked}
          label={t('Job roles and salary ranges')}
          salariesPagePath={salariesPagePath}
        />
      </Hidden>

      <Hidden below="tablet">
        <DesktopSalariesList
          paginatedSalaryRanges={paginatedSalaryRanges}
          trackRoleClicked={trackRoleClicked}
          salariesPagePath={salariesPagePath}
        />
      </Hidden>

      {totalPages > 1 && (
        <Pagination
          page={currentPage}
          total={totalPages}
          linkProps={({ page }) => ({
            href: `#${page}`,
            onClick: (e) => {
              e.preventDefault();
              setCurrentPage(page);
              scrollToTop();
            },
          })}
          label={t('Pagination of results')}
          nextLabel={t('Next')}
          previousLabel={t('Previous')}
        />
      )}
    </Stack>
  );
};
